import React, { useState, Component } from 'react';
import { ShareIcon, CheckIcon } from 'lucide-react';
interface SocialShareProps {
  url?: string;
  title?: string;
  description?: string;
  hashtags?: string[];
}
export function SocialShare({
  url = typeof window !== 'undefined' ? window.location.href : '',
  title = 'Check this out on CryptoTopReviews',
  description = 'Trusted cryptocurrency reviews and market data',
  hashtags = ['crypto', 'cryptocurrency', 'bitcoin']
}: SocialShareProps) {
  const [copied, setCopied] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  const hashtagString = hashtags.join(',');
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&hashtags=${hashtagString}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };
  const handleShare = (platform: keyof typeof shareLinks) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    setShowMenu(false);
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  return <div className="relative" data-id="element-170">
      <button onClick={() => setShowMenu(!showMenu)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-171">
        <ShareIcon size={18} data-id="element-172" />
        <span data-id="element-173">Share</span>
      </button>

      {showMenu && <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} data-id="element-174" />

          {/* Share Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden" data-id="element-175">
            <div className="p-3 border-b border-gray-200" data-id="element-176">
              <p className="text-sm font-semibold text-gray-900" data-id="element-177">
                Share this page
              </p>
            </div>

            <div className="p-2" data-id="element-178">
              {/* Twitter/X */}
              <button onClick={() => handleShare('twitter')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left" data-id="element-179">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-180">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-181">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-182" />
                  </svg>
                </div>
                <div className="flex-1" data-id="element-183">
                  <div className="text-sm font-medium text-gray-900" data-id="element-184">
                    Twitter / X
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-185">Share on X</div>
                </div>
              </button>

              {/* WhatsApp */}
              <button onClick={() => handleShare('whatsapp')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left" data-id="element-186">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-187">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-188">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-189" />
                  </svg>
                </div>
                <div className="flex-1" data-id="element-190">
                  <div className="text-sm font-medium text-gray-900" data-id="element-191">
                    WhatsApp
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-192">
                    Share via WhatsApp
                  </div>
                </div>
              </button>

              {/* Telegram */}
              <button onClick={() => handleShare('telegram')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left" data-id="element-193">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-194">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-195">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-196" />
                  </svg>
                </div>
                <div className="flex-1" data-id="element-197">
                  <div className="text-sm font-medium text-gray-900" data-id="element-198">
                    Telegram
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-199">Share on Telegram</div>
                </div>
              </button>

              {/* Facebook */}
              <button onClick={() => handleShare('facebook')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left" data-id="element-200">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-201">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-202">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-203" />
                  </svg>
                </div>
                <div className="flex-1" data-id="element-204">
                  <div className="text-sm font-medium text-gray-900" data-id="element-205">
                    Facebook
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-206">Share on Facebook</div>
                </div>
              </button>

              {/* LinkedIn */}
              <button onClick={() => handleShare('linkedin')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left" data-id="element-207">
                <div className="w-8 h-8 bg-blue-700 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-208">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-209">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-210" />
                  </svg>
                </div>
                <div className="flex-1" data-id="element-211">
                  <div className="text-sm font-medium text-gray-900" data-id="element-212">
                    LinkedIn
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-213">Share on LinkedIn</div>
                </div>
              </button>

              {/* Reddit */}
              <button onClick={() => handleShare('reddit')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left" data-id="element-214">
                <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-215">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-216">
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" data-id="element-217" />
                  </svg>
                </div>
                <div className="flex-1" data-id="element-218">
                  <div className="text-sm font-medium text-gray-900" data-id="element-219">
                    Reddit
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-220">Share on Reddit</div>
                </div>
              </button>

              {/* Email */}
              <button onClick={() => handleShare('email')} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left" data-id="element-221">
                <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-222">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-223">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-224" />
                  </svg>
                </div>
                <div className="flex-1" data-id="element-225">
                  <div className="text-sm font-medium text-gray-900" data-id="element-226">Email</div>
                  <div className="text-xs text-gray-500" data-id="element-227">Share via email</div>
                </div>
              </button>

              {/* Copy Link */}
              <button onClick={handleCopyLink} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left border-t border-gray-200 mt-2 pt-3" data-id="element-228">
                <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-229">
                  {copied ? <CheckIcon className="w-5 h-5 text-green-600" data-id="element-230" /> : <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-231">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-232" />
                    </svg>}
                </div>
                <div className="flex-1" data-id="element-233">
                  <div className="text-sm font-medium text-gray-900" data-id="element-234">
                    {copied ? 'Link copied!' : 'Copy link'}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-235">
                    {copied ? 'Ready to paste' : 'Copy to clipboard'}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </>}
    </div>;
}